import React, { useState } from "react";
import { toast } from "sonner";
import { Flask } from "@phosphor-icons/react";
import api from "@/lib/api";
import { US_STATES } from "@/components/shared/StateCheckboxSelector";
import { AGE_BUCKET_OPTIONS, CASE_TYPE_OPTIONS } from "@/pages/landing/quizPresets";
import { TryLeadResult } from "@/components/traffic/TryLeadResult";

const SEL = "w-full border-2 border-black bg-white px-2 py-1.5 text-sm";
const YES_NO = [["", "Any"], ["yes", "Yes"], ["no", "No"]];
const EMPTY = {
  page_id: "", state: "", case_type: "", affiliate_id: "", physically_injured: "", currently_represented: "", at_fault: "",
  incident_age_bucket: "",
};

function Pick({ label, value, onChange, testId, children }) {
  return (
    <label className="block min-w-0">
      <span className="label-mono">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={SEL} data-testid={testId}>{children}</select>
    </label>
  );
}

// Try a lead: pick a page and answers, see which buyer and thank-you page it would get. Nothing is sent.
export function TryLeadCard({ pages, partners }) {
  const [f, setF] = useState(EMPTY);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => { setF((x) => ({ ...x, [k]: v })); setRes(null); };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/traffic-access/try-lead", f);
      setRes(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't run the check");
    } finally { setBusy(false); }
  };
  return (
    <section className="mb-5 border-2 border-black bg-white p-4" data-testid="try-lead-card">
      <div className="flex items-center gap-2"><Flask size={18} weight="bold" /><div className="font-chivo text-lg font-black uppercase tracking-tighter">Try a lead</div></div>
      <p className="mb-3 text-xs text-zinc-600">See which buyer a visitor would go to and which thank-you page they&apos;d see. Nothing is saved or sent.</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Pick label="Page" value={f.page_id} onChange={set("page_id")} testId="try-lead-page">
          <option value="">Pick a page…</option>
          {pages.map((p) => <option key={p.id} value={p.id}>{p.title}{p.kind === "own" ? ` (${p.firm_name})` : ""}{p.is_test ? " · test" : ""}</option>)}
        </Pick>
        <Pick label="Visitor's state" value={f.state} onChange={set("state")} testId="try-lead-state">
          <option value="">Pick a state…</option>
          {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Pick>
        <Pick label="Accident type" value={f.case_type} onChange={set("case_type")} testId="try-lead-case">
          <option value="">Page default</option>
          {CASE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Pick>
        <Pick label="Traffic from" value={f.affiliate_id} onChange={set("affiliate_id")} testId="try-lead-source">
          <option value="">Direct / media buyer</option>
          {partners.filter((m) => m.kind === "affiliate").map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </Pick>
        <Pick label="Hurt?" value={f.physically_injured} onChange={set("physically_injured")} testId="try-lead-injured">
          {YES_NO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Pick>
        <Pick label="Already has a lawyer?" value={f.currently_represented} onChange={set("currently_represented")} testId="try-lead-lawyer">
          {YES_NO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Pick>
        <Pick label="At fault?" value={f.at_fault} onChange={set("at_fault")} testId="try-lead-fault">
          {YES_NO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Pick>
        <Pick label="When did it happen?" value={f.incident_age_bucket} onChange={set("incident_age_bucket")} testId="try-lead-when">
          <option value="">Any</option>
          {AGE_BUCKET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Pick>
        <div className="flex items-end sm:col-span-2 lg:col-span-3">
          <button type="button" onClick={run} disabled={busy || !f.page_id || !f.state} data-testid="try-lead-run"
                  className="w-full border-2 border-black bg-black px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-100 hover:bg-emerald-700 disabled:opacity-40">
            {busy ? "Checking…" : "Check this lead"}
          </button>
        </div>
      </div>
      {res && <TryLeadResult res={res} />}
    </section>
  );
}
